import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { OFFICES, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_BG =
  "https://images.pexels.com/photos/36405564/pexels-photo-36405564.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Contact() {
  const location = useLocation();
  const prefillService = location.state?.service || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    country: "Saudi Arabia",
    service: prefillService,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We will respond within 1 business day.");
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        country: "Saudi Arabia",
        service: "",
        message: "",
      });
    } catch {
      toast.error("Could not send. Try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Notion Minds Systems — Riyadh, Saudi Arabia"
        description="Contact Notion Minds Systems in Riyadh, Saudi Arabia — speak with a senior partner about your technology transformation. Call +966 50 694 7879."
        canonical="https://notionminds.sa/contact"
      />
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={CONTACT_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
          <p className="font-mono-accent text-xs text-[#d4af37] mb-5">[ Contact ]</p>
          <h1 className="font-display text-5xl lg:text-7xl font-light leading-[1.05] max-w-4xl">
            Start a <span className="text-gradient-gold font-semibold">consequential</span> conversation.
          </h1>
          <p className="mt-8 text-white/65 text-lg max-w-2xl">
            Tell us what you're trying to achieve. A senior partner will respond
            within one business day — no gatekeeping.
          </p>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-[#0a0a0e] border border-[#24242a] p-8 lg:p-12 space-y-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field
              label="Full name *"
              testid="contact-name"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
            />
            <Field
              label="Work email *"
              testid="contact-email"
              type="email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field
              label="Company"
              testid="contact-company"
              value={form.company}
              onChange={(v) => setForm({ ...form, company: v })}
            />
            <Field
              label="Phone"
              testid="contact-phone"
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="font-mono-accent text-[10px] text-white/50 mb-2 block">Country</label>
              <select
                data-testid="contact-country"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] px-4 py-3 text-sm outline-none transition-colors"
              >
                <option className="bg-[#0a0a0e]">Saudi Arabia</option>
                <option className="bg-[#0a0a0e]">Oman</option>
                <option className="bg-[#0a0a0e]">Qatar</option>
                <option className="bg-[#0a0a0e]">UAE</option>
                <option className="bg-[#0a0a0e]">Kuwait</option>
                <option className="bg-[#0a0a0e]">Bahrain</option>
                <option className="bg-[#0a0a0e]">Other</option>
              </select>
            </div>
            <div>
              <label className="font-mono-accent text-[10px] text-white/50 mb-2 block">Service of interest</label>
              <select
                data-testid="contact-service"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] px-4 py-3 text-sm outline-none transition-colors"
              >
                <option value="" className="bg-[#0a0a0e]">Select a service</option>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title} className="bg-[#0a0a0e]">{s.title}</option>
                ))}
                <option className="bg-[#0a0a0e]">Not sure yet</option>
              </select>
            </div>
          </div>
          <div>
            <label className="font-mono-accent text-[10px] text-white/50 mb-2 block">How can we help? *</label>
            <textarea
              data-testid="contact-message"
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] px-4 py-3 text-sm outline-none resize-none transition-colors"
              placeholder="Tell us about the business outcome you're chasing..."
            />
          </div>
          <button
            type="submit"
            data-testid="contact-submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-[#d4af37] text-black px-7 py-4 font-semibold hover:bg-[#e5c059] transition-colors disabled:opacity-60"
          >
            {submitting ? "Sending..." : "Send message"} <ArrowUpRight className="w-4 h-4" />
          </button>
        </form>

        <aside className="lg:col-span-5 space-y-6">
          <div className="bg-[#0a0a0e] border border-[#24242a] p-8">
            <p className="font-mono-accent text-xs text-[#d4af37] mb-5">Direct lines</p>
            <div className="space-y-4 text-sm">
              <a href="mailto:hello@notionminds.sa" className="flex items-center gap-3 text-white/80 hover:text-[#d4af37]">
                <Mail className="w-4 h-4" /> hello@notionminds.sa
              </a>
              <div className="flex items-center gap-3 text-white/80">
                <Phone className="w-4 h-4" /> +966 50 694 7879
              </div>
            </div>
          </div>

          {OFFICES.map((o, i) => (
            <div key={o.city} data-testid={`contact-office-${o.city.toLowerCase()}`} className="bg-[#0a0a0e] border border-[#24242a] overflow-hidden">
              <div className="p-8">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-mono-accent text-[10px] text-[#d4af37] mb-2">Office 0{i + 1}</p>
                    <h3 className="font-display text-xl font-light">{o.city}, {o.country}</h3>
                  </div>
                  <MapPin className="w-4 h-4 text-white/40" />
                </div>
                <div className="text-xs text-white/55 space-y-1.5 mt-4">
                  <p>{o.address}</p>
                  {o.address2 && <p>{o.address2}</p>}
                  {o.zip && <p>ZIP {o.zip}</p>}
                  <p>{o.phone}</p>
                  <p>{o.email}</p>
                </div>
              </div>
              <div className="relative aspect-[4/3] w-full border-t border-[#24242a] bg-[#050505]" data-testid={`office-map-${o.city.toLowerCase()}`}>
                <iframe
                  title={`${o.city} office map`}
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(
                    o.mapQuery || `${o.address}, ${o.address2 || ""}, ${o.city} ${o.zip || ""}, ${o.country}`
                  )}&t=&z=16&ie=UTF8&iwloc=&output=embed`}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="absolute inset-0 w-full h-full grayscale contrast-125 opacity-80 hover:opacity-100 transition-opacity"
                  style={{ border: 0, filter: "grayscale(100%) invert(90%) hue-rotate(180deg)" }}
                />
              </div>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  o.mapQuery || `${o.address}, ${o.address2 || ""}, ${o.city} ${o.zip || ""}, ${o.country}`
                )}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`office-directions-${o.city.toLowerCase()}`}
                className="flex items-center justify-between px-8 py-4 border-t border-[#24242a] text-sm text-white/70 hover:text-[#d4af37] hover:bg-white/[0.03] transition-colors"
              >
                <span className="inline-flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Open in Google Maps
                </span>
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </aside>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="font-mono-accent text-[10px] text-white/50 mb-2 block">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] px-4 py-3 text-sm outline-none transition-colors"
      />
    </div>
  );
}
